import React, { useMemo, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Stack,
  useTheme,
  Collapse,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  ShowChart as ShowChartIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
} from "recharts";

import dashboardService from "../../../../services/dashboardService";

/** Offset so anchored sections clear the app header when scrolled into view */
const PROGRESS_SECTION_SCROLL_MARGIN_PX = 88;

const PROGRESS_TOP_ANCHOR_ID = "progress-over-time-top";

const PROGRESS_SECTIONS = [
  { id: "progress-section-training", label: "Training volume" },
  { id: "progress-section-scale-vs-load", label: "Scale vs load" },
  { id: "progress-section-top-lifts", label: "Top lifts" },
  { id: "progress-section-weekly-weight", label: "Weekly weight avg" },
  { id: "progress-section-daily-weight", label: "Daily weight" },
  { id: "progress-section-nutrition", label: "Nutrition" },
  { id: "progress-section-reports", label: "Weekly reports" },
];

const DAY_OPTIONS = [30, 90, 180, 365];

/** Matches Recharts line colors for daily nutrition */
const NUTRITION_SERIES_COLORS = {
  protein: "#4DB6AC",
  carbs: "#FFB74D",
  fats: "#F06292",
  fiber: "#66BB6A",
};

/** Daily nutrition chart: which series to plot */
const NUTRITION_SERIES_OPTIONS = [
  { value: "all", label: "All", aria: "All macros and fiber", seriesColor: null },
  {
    value: "protein",
    label: "Protein",
    aria: "Protein only",
    seriesColor: NUTRITION_SERIES_COLORS.protein,
  },
  {
    value: "carbs",
    label: "Carbs",
    aria: "Carbs only",
    seriesColor: NUTRITION_SERIES_COLORS.carbs,
  },
  {
    value: "fats",
    label: "Fats",
    aria: "Fats only",
    seriesColor: NUTRITION_SERIES_COLORS.fats,
  },
  {
    value: "fiber",
    label: "Fiber",
    aria: "Fiber only",
    seriesColor: NUTRITION_SERIES_COLORS.fiber,
  },
];

function shortDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Trailing window mean; skips null/undefined so missing check-in fields do not count as zero. */
function rollingAverageSeries(values, windowSize = 7) {
  if (!values.length) return [];
  return values.map((_, i) => {
    const start = Math.max(0, i - windowSize + 1);
    const slice = values.slice(start, i + 1);
    const nums = slice
      .map((x) => (x == null ? null : Number(x)))
      .filter((x) => x != null && Number.isFinite(x));
    if (nums.length === 0) return null;
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    return Math.round(avg * 10) / 10;
  });
}

function weekLabel(weekStart) {
  if (!weekStart) return "";
  const d = new Date(weekStart);
  return Number.isNaN(d.getTime()) ? String(weekStart) : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function weeklyReportStatusLabel(status) {
  if (!status) return "—";
  if (status === "unprocessed") return "Needs review";
  if (status === "processed") return "Processed";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Distinct strokes for multi-exercise strength chart */
const STRENGTH_LINE_COLORS = [
  "#0077C8",
  "#C40F0D",
  "#4DB6AC",
  "#E28413",
  "#9575CD",
  "#F06292",
];

const emptyChart = (
  <Box sx={{ py: 4, textAlign: "center" }}>
    <Typography variant="body2" color="text.secondary">
      No data in this range
    </Typography>
  </Box>
);

function ProgressSectionFooter({ onBackToTop }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        mt: 2,
        pt: 1.5,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Button
        type="button"
        size="small"
        startIcon={<KeyboardArrowUp />}
        onClick={onBackToTop}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Back to top
      </Button>
    </Box>
  );
}

function escapeCsvCell(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

/** One row per check-in; week avg / count repeated for spreadsheet pivots */
function buildWeightWeeklyCsv(weeklyRows) {
  const header = [
    "week_start",
    "week_avg_lbs",
    "week_checkin_count",
    "date",
    "weight_lbs",
  ];
  const lines = [header.join(",")];
  weeklyRows.forEach((w) => {
    if (!w.entries || w.entries.length === 0) {
      lines.push(
        [
          escapeCsvCell(w.weekStart),
          escapeCsvCell(w.avgWeightLbs ?? ""),
          escapeCsvCell(w.count ?? 0),
          "",
          "",
        ].join(","),
      );
    } else {
      w.entries.forEach((e) => {
        const dateOnly = e.date ? String(e.date).slice(0, 10) : "";
        lines.push(
          [
            escapeCsvCell(w.weekStart),
            escapeCsvCell(w.avgWeightLbs ?? ""),
            escapeCsvCell(w.count ?? w.entries.length),
            escapeCsvCell(dateOnly),
            escapeCsvCell(e.weight),
          ].join(","),
        );
      });
    }
  });
  return lines.join("\n");
}

const ClientProgressPage = () => {
  const { uid, organizationId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [days, setDays] = React.useState(180);
  const [expandedWeekKey, setExpandedWeekKey] = useState(null);
  const [nutritionSeriesMode, setNutritionSeriesMode] = useState("all");

  const handleDaysChange = useCallback((_e, value) => {
    if (value != null) setDays(value);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["client-progress", organizationId, uid, days],
    queryFn: () =>
      dashboardService.getClientProgress(organizationId, uid, { days }),
    enabled: Boolean(organizationId && uid),
    staleTime: 5 * 60 * 1000,
  });

  const meta = data?.meta;
  const workoutsChart = useMemo(() => {
    const rows = data?.workouts?.byWeek || [];
    return rows.map((w) => ({
      ...w,
      label: weekLabel(w.weekStart),
    }));
  }, [data]);

  const macrosChart = useMemo(() => {
    const rows = data?.nutrition?.daily || [];
    const sorted = [...rows].sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
    const protein = sorted.map((r) => r.protein);
    const carbs = sorted.map((r) => r.carbs);
    const fats = sorted.map((r) => r.fats);
    const fiber = sorted.map((r) => r.fiber);
    const proteinAvg7 = rollingAverageSeries(protein);
    const carbsAvg7 = rollingAverageSeries(carbs);
    const fatsAvg7 = rollingAverageSeries(fats);
    const fiberAvg7 = rollingAverageSeries(fiber);
    return sorted.map((r, i) => ({
      ...r,
      label: shortDateLabel(r.date),
      proteinAvg7: proteinAvg7[i],
      carbsAvg7: carbsAvg7[i],
      fatsAvg7: fatsAvg7[i],
      fiberAvg7: fiberAvg7[i],
    }));
  }, [data]);

  const weightChart = useMemo(() => {
    const rows = data?.weight?.daily || [];
    const sorted = [...rows].sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
    const weights = sorted.map((r) => r.weight);
    const weightAvg7 = rollingAverageSeries(weights);
    return sorted.map((r, i) => ({
      ...r,
      label: shortDateLabel(r.date),
      weightAvg7: weightAvg7[i],
    }));
  }, [data]);

  const weeklyWeightRows = useMemo(() => data?.weight?.weekly ?? [], [data]);

  /** Chronological series for weekly avg chart (table below is newest-first) */
  const weeklyWeightChart = useMemo(() => {
    return [...weeklyWeightRows]
      .filter((w) => w.avgWeightLbs != null && Number.isFinite(w.avgWeightLbs))
      .sort((a, b) => a.weekStart.localeCompare(b.weekStart))
      .map((w) => ({
        weekStart: w.weekStart,
        label: weekLabel(w.weekStart),
        avgWeightLbs: w.avgWeightLbs,
        count: w.count ?? 0,
      }));
  }, [weeklyWeightRows]);

  const reports = data?.reports || [];
  const nutritionGoals = data?.goals?.nutrition;

  const correlationChart = useMemo(() => {
    const rows = data?.correlation?.weekly || [];
    return rows.map((r) => ({
      ...r,
      label: weekLabel(r.weekStart),
    }));
  }, [data]);

  const hasCorrelationData = useMemo(
    () =>
      correlationChart.some(
        (r) => r.avgWeightLbs != null || r.maxWeightLbs != null,
      ),
    [correlationChart],
  );

  const strengthByExercise = data?.strength?.byExercise || [];

  const topLiftsChart = useMemo(() => {
    if (!strengthByExercise.length) return [];
    const weekSet = new Set();
    strengthByExercise.forEach((ex) => {
      ex.weekly?.forEach((w) => weekSet.add(w.weekStart));
    });
    const weeks = [...weekSet].sort();
    return weeks.map((wk) => {
      const row = { label: weekLabel(wk), weekStart: wk };
      strengthByExercise.forEach((ex) => {
        const pt = ex.weekly?.find((x) => x.weekStart === wk);
        row[ex.exerciseId] =
          pt?.maxWeightLbs != null ? pt.maxWeightLbs : null;
      });
      return row;
    });
  }, [strengthByExercise]);

  const hasTopLiftsData = useMemo(
    () =>
      topLiftsChart.some((row) =>
        strengthByExercise.some((ex) => row[ex.exerciseId] != null),
      ),
    [topLiftsChart, strengthByExercise],
  );

  const backToSummary = useCallback(() => {
    navigate(`/home/clients/summary/${uid}/${organizationId}`);
  }, [navigate, uid, organizationId]);

  const handleExportWeeklyWeightCsv = useCallback(() => {
    if (!weeklyWeightRows.length) return;
    const csv = buildWeightWeeklyCsv(weeklyWeightRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const namePart =
      [meta?.lastName, meta?.firstName, uid].filter(Boolean).join("-") || "client";
    a.download = `weight-weekly-${namePart}-${days}d.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [weeklyWeightRows, meta?.lastName, meta?.firstName, uid, days]);

  const toggleWeekExpanded = useCallback((weekStart) => {
    setExpandedWeekKey((prev) => (prev === weekStart ? null : weekStart));
  }, []);

  const handleNutritionSeriesChange = useCallback((_e, value) => {
    if (value != null) setNutritionSeriesMode(value);
  }, []);

  const scrollToProgressTop = useCallback(() => {
    document
      .getElementById(PROGRESS_TOP_ANCHOR_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const scrollToProgressSection = useCallback((sectionId) => {
    document
      .getElementById(sectionId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  if (!organizationId || !uid) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="warning">Missing client or organization.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={backToSummary}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Back to summary
        </Button>
      </Stack>

      <Stack
        id={PROGRESS_TOP_ANCHOR_ID}
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 1, scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px` }}
      >
        <ShowChartIcon color="primary" sx={{ fontSize: 36 }} />
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary">
            Progress over time
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {isLoading
              ? "Loading…"
              : `${meta?.firstName || ""} ${meta?.lastName || ""}`.trim() ||
                "Client"}
            {meta?.coachName ? ` · Coach: ${meta.coachName}` : ""}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Date range
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={days}
          onChange={handleDaysChange}
        >
          {DAY_OPTIONS.map((d) => (
            <ToggleButton key={d} value={d}>
              {d}d
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || "Failed to load progress data"}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" component="span" display="block" sx={{ mb: 0.75 }}>
          <strong>Reading weight + strength together:</strong> scale weight can rise while
          strength still improves—common when gaining muscle or holding more water from
          training and carbs. Compare weekly <em>average</em> scale weight with{" "}
          <em>heaviest loads</em> logged and the top-lift trends below.
        </Typography>
        <Typography variant="body2" component="span" color="text.secondary">
          This is context for coaching conversations, not a medical interpretation.
        </Typography>
      </Alert>

      <Paper
        variant="outlined"
        sx={{
          position: "sticky",
          top: 8,
          zIndex: 2,
          p: 1.5,
          mb: 3,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Jump to section
        </Typography>
        <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap sx={{ rowGap: 0.5 }}>
          {PROGRESS_SECTIONS.map((s) => (
            <Button
              key={s.id}
              type="button"
              size="small"
              variant="text"
              color="primary"
              onClick={() => scrollToProgressSection(s.id)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {s.label}
            </Button>
          ))}
        </Stack>
      </Paper>

      {/* Training */}
      <Card
        id="progress-section-training"
        sx={{
          mb: 3,
          boxShadow: theme.shadows[1],
          scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px`,
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Training volume & consistency
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Workout days per week and total training volume from completed sets. Strength
            (max load per week and per exercise) is in the next sections so you can line it
            up with scale weight and nutrition.
          </Typography>
          {isLoading ? (
            emptyChart
          ) : workoutsChart.length === 0 ? (
            emptyChart
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={workoutsChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="workoutDays" name="Workout days" fill={theme.palette.secondary.main} />
                <Line yAxisId="right" type="monotone" dataKey="totalVolume" name="Volume (lb×reps)" stroke={theme.palette.primary.main} dot={false} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <ProgressSectionFooter onBackToTop={scrollToProgressTop} />
        </CardContent>
      </Card>

      {/* Scale weight vs training load (weekly) */}
      <Card
        id="progress-section-scale-vs-load"
        sx={{
          mb: 3,
          boxShadow: theme.shadows[1],
          scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px`,
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Scale weight vs training load (by week)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Left axis: average scale weight from check-ins that week. Right axis: heaviest
            single weight logged in any exercise that week (proxy for “are they moving
            heavier loads?”).
          </Typography>
          {isLoading ? (
            emptyChart
          ) : !hasCorrelationData ? (
            emptyChart
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={correlationChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  yAxisId="scale"
                  tick={{ fontSize: 11 }}
                  domain={["auto", "auto"]}
                  label={{ value: "lb", angle: -90, position: "insideLeft" }}
                />
                <YAxis
                  yAxisId="load"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  domain={["auto", "auto"]}
                  label={{ value: "lb", angle: 90, position: "insideRight" }}
                />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="scale"
                  type="monotone"
                  dataKey="avgWeightLbs"
                  name="Avg scale weight (lb)"
                  stroke={theme.palette.primary.main}
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                />
                <Line
                  yAxisId="load"
                  type="monotone"
                  dataKey="maxWeightLbs"
                  name="Heaviest load (lb)"
                  stroke={theme.palette.secondary.main}
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          <ProgressSectionFooter onBackToTop={scrollToProgressTop} />
        </CardContent>
      </Card>

      {/* Top lifts — weekly max weight */}
      <Card
        id="progress-section-top-lifts"
        sx={{
          mb: 3,
          boxShadow: theme.shadows[1],
          scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px`,
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Top lifts — weekly heaviest weight
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Up to six exercises with the most completed volume in this range. Lines show
            the max weight logged each week for each (completed sets only).
          </Typography>
          {isLoading ? (
            emptyChart
          ) : !hasTopLiftsData ? (
            emptyChart
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={topLiftsChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  domain={["auto", "auto"]}
                  label={{ value: "lb", angle: -90, position: "insideLeft" }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {strengthByExercise.map((ex, i) => (
                  <Line
                    key={ex.exerciseId}
                    type="monotone"
                    dataKey={ex.exerciseId}
                    name={ex.exerciseName}
                    stroke={STRENGTH_LINE_COLORS[i % STRENGTH_LINE_COLORS.length]}
                    dot={false}
                    strokeWidth={2}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
          <ProgressSectionFooter onBackToTop={scrollToProgressTop} />
        </CardContent>
      </Card>

      {/* Weekly weight averages — sheet-style + export */}
      <Card
        id="progress-section-weekly-weight"
        sx={{
          mb: 3,
          boxShadow: theme.shadows[1],
          scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px`,
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Weekly weight average (check-ins)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Average scale weight per calendar week (Mon–Sun). Chart and table use the same
                averages—expand a week for each check-in. Export matches the table for Excel or
                Google Sheets.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              disabled={isLoading || weeklyWeightRows.length === 0}
              onClick={handleExportWeeklyWeightCsv}
              sx={{ textTransform: "none", fontWeight: 600, flexShrink: 0 }}
            >
              Export CSV
            </Button>
          </Stack>
          {isLoading ? (
            emptyChart
          ) : weeklyWeightRows.length === 0 ? (
            emptyChart
          ) : (
            <>
              {weeklyWeightChart.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Trend (weekly average, lb)
                  </Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={weeklyWeightChart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        domain={["auto", "auto"]}
                        label={{ value: "lb", angle: -90, position: "insideLeft" }}
                      />
                      <Tooltip
                        formatter={(value, name, props) => {
                          if (name === "avgWeightLbs") {
                            const c = props?.payload?.count;
                            return [
                              `${value} lb${c != null ? ` (${c} check-in${c === 1 ? "" : "s"})` : ""}`,
                              "Avg",
                            ];
                          }
                          return [value, name];
                        }}
                        labelFormatter={(label) => `Week of ${label}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="avgWeightLbs"
                        name="Weekly avg (lb)"
                        stroke={theme.palette.primary.main}
                        dot={{ r: 3 }}
                        strokeWidth={2}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : null}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48} />
                    <TableCell>Week of</TableCell>
                    <TableCell align="right">Avg (lb)</TableCell>
                    <TableCell align="right">Check-ins</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...weeklyWeightRows].reverse().map((w) => {
                    const open = expandedWeekKey === w.weekStart;
                    const hasDetail = (w.entries?.length ?? 0) > 0;
                    return (
                      <React.Fragment key={w.weekStart}>
                        <TableRow hover>
                          <TableCell padding="checkbox">
                            <IconButton
                              size="small"
                              aria-label={open ? "Collapse week" : "Expand week"}
                              disabled={!hasDetail}
                              onClick={() => toggleWeekExpanded(w.weekStart)}
                            >
                              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{weekLabel(w.weekStart)}</TableCell>
                          <TableCell align="right">
                            {w.avgWeightLbs != null ? w.avgWeightLbs : "—"}
                          </TableCell>
                          <TableCell align="right">{w.count ?? 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{ py: 0, borderBottom: open ? undefined : 0 }}
                          >
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 2, px: 1 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mb: 1 }}
                                >
                                  Daily check-ins this week
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Date</TableCell>
                                      <TableCell align="right">Weight (lb)</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(w.entries || []).map((e, i) => (
                                      <TableRow key={`${e.date}-${i}`}>
                                        <TableCell>{shortDateLabel(e.date)}</TableCell>
                                        <TableCell align="right">{e.weight}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
          <ProgressSectionFooter onBackToTop={scrollToProgressTop} />
        </CardContent>
      </Card>

      {/* Weight */}
      <Card
        id="progress-section-daily-weight"
        sx={{
          mb: 3,
          boxShadow: theme.shadows[1],
          scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px`,
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Weight (daily check-ins)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Bold line: 7-day moving average (trend). Faint line: each check-in. Log scale does not
            smooth noise and is a poor fit for body weight—averaging is clearer.
          </Typography>
          {isLoading ? (
            emptyChart
          ) : weightChart.length === 0 ? (
            emptyChart
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={weightChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="weight"
                  name="Daily (lb)"
                  stroke={theme.palette.primary.main}
                  dot={false}
                  strokeWidth={1}
                  strokeOpacity={0.35}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="weightAvg7"
                  name="7-day average (lb)"
                  stroke={theme.palette.primary.main}
                  dot={false}
                  strokeWidth={2.5}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          )}
          <ProgressSectionFooter onBackToTop={scrollToProgressTop} />
        </CardContent>
      </Card>

      {/* Macros */}
      <Card
        id="progress-section-nutrition"
        sx={{
          mb: 3,
          boxShadow: theme.shadows[1],
          scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px`,
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Daily nutrition (check-ins)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            <strong>All</strong> shows 7-day moving averages only (one line per macro) so trends are
            readable. Choosing a single macro shows <strong>daily</strong> (faint) plus{" "}
            <strong>7-day average</strong> (bold). Missing macros are gaps—not zeros.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Series
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={nutritionSeriesMode}
            onChange={handleNutritionSeriesChange}
            sx={{ mb: 2, flexWrap: "wrap" }}
          >
            {NUTRITION_SERIES_OPTIONS.map((opt) => {
              const c = opt.seriesColor;
              const primary = theme.palette.primary.main;
              return (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  aria-label={opt.aria}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    ...(c
                      ? {
                          color: c,
                          borderColor: alpha(c, 0.55),
                          "&:hover": {
                            backgroundColor: alpha(c, 0.08),
                            borderColor: c,
                          },
                          "&.Mui-selected": {
                            color: c,
                            backgroundColor: alpha(c, 0.2),
                            borderColor: c,
                            "&:hover": {
                              backgroundColor: alpha(c, 0.28),
                              borderColor: c,
                            },
                          },
                        }
                      : {
                          "&.Mui-selected": {
                            color: primary,
                            backgroundColor: alpha(primary, 0.12),
                            borderColor: primary,
                            "&:hover": {
                              backgroundColor: alpha(primary, 0.18),
                              borderColor: primary,
                            },
                          },
                        }),
                  }}
                >
                  {opt.label}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
          {isLoading ? (
            emptyChart
          ) : macrosChart.length === 0 ? (
            emptyChart
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={macrosChart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "protein") && (
                  <>
                    {nutritionSeriesMode === "protein" && (
                      <Line
                        type="monotone"
                        dataKey="protein"
                        name="Protein (g)"
                        stroke={NUTRITION_SERIES_COLORS.protein}
                        dot={false}
                        strokeWidth={1}
                        strokeOpacity={0.35}
                        connectNulls={false}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="proteinAvg7"
                      name={
                        nutritionSeriesMode === "all"
                          ? "Protein (7d avg)"
                          : "Protein (7-day avg)"
                      }
                      stroke={NUTRITION_SERIES_COLORS.protein}
                      dot={false}
                      strokeWidth={nutritionSeriesMode === "all" ? 2 : 2.5}
                      connectNulls
                    />
                  </>
                )}
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "carbs") && (
                  <>
                    {nutritionSeriesMode === "carbs" && (
                      <Line
                        type="monotone"
                        dataKey="carbs"
                        name="Carbs (g)"
                        stroke={NUTRITION_SERIES_COLORS.carbs}
                        dot={false}
                        strokeWidth={1}
                        strokeOpacity={0.35}
                        connectNulls={false}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="carbsAvg7"
                      name={
                        nutritionSeriesMode === "all" ? "Carbs (7d avg)" : "Carbs (7-day avg)"
                      }
                      stroke={NUTRITION_SERIES_COLORS.carbs}
                      dot={false}
                      strokeWidth={nutritionSeriesMode === "all" ? 2 : 2.5}
                      connectNulls
                    />
                  </>
                )}
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "fats") && (
                  <>
                    {nutritionSeriesMode === "fats" && (
                      <Line
                        type="monotone"
                        dataKey="fats"
                        name="Fats (g)"
                        stroke={NUTRITION_SERIES_COLORS.fats}
                        dot={false}
                        strokeWidth={1}
                        strokeOpacity={0.35}
                        connectNulls={false}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="fatsAvg7"
                      name={
                        nutritionSeriesMode === "all" ? "Fats (7d avg)" : "Fats (7-day avg)"
                      }
                      stroke={NUTRITION_SERIES_COLORS.fats}
                      dot={false}
                      strokeWidth={nutritionSeriesMode === "all" ? 2 : 2.5}
                      connectNulls
                    />
                  </>
                )}
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "fiber") && (
                  <>
                    {nutritionSeriesMode === "fiber" && (
                      <Line
                        type="monotone"
                        dataKey="fiber"
                        name="Fiber (g)"
                        stroke={NUTRITION_SERIES_COLORS.fiber}
                        dot={false}
                        strokeWidth={1}
                        strokeOpacity={0.35}
                        connectNulls={false}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="fiberAvg7"
                      name={
                        nutritionSeriesMode === "all" ? "Fiber (7d avg)" : "Fiber (7-day avg)"
                      }
                      stroke={NUTRITION_SERIES_COLORS.fiber}
                      dot={false}
                      strokeWidth={nutritionSeriesMode === "all" ? 2 : 2.5}
                      connectNulls={false}
                    />
                  </>
                )}
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "protein") &&
                  nutritionGoals?.proteins != null && (
                    <ReferenceLine
                      y={nutritionGoals.proteins}
                      stroke={NUTRITION_SERIES_COLORS.protein}
                      strokeDasharray="4 4"
                      label={{
                        value: "Protein goal",
                        position: "insideTopRight",
                        fill: NUTRITION_SERIES_COLORS.protein,
                      }}
                    />
                  )}
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "carbs") &&
                  nutritionGoals?.carbs != null && (
                    <ReferenceLine
                      y={nutritionGoals.carbs}
                      stroke={NUTRITION_SERIES_COLORS.carbs}
                      strokeDasharray="4 4"
                      label={{
                        value: "Carbs goal",
                        position: "insideTopRight",
                        fill: NUTRITION_SERIES_COLORS.carbs,
                      }}
                    />
                  )}
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "fats") &&
                  nutritionGoals?.fats != null && (
                    <ReferenceLine
                      y={nutritionGoals.fats}
                      stroke={NUTRITION_SERIES_COLORS.fats}
                      strokeDasharray="4 4"
                      label={{
                        value: "Fats goal",
                        position: "insideTopRight",
                        fill: NUTRITION_SERIES_COLORS.fats,
                      }}
                    />
                  )}
                {(nutritionSeriesMode === "all" || nutritionSeriesMode === "fiber") &&
                  nutritionGoals?.fiber != null && (
                    <ReferenceLine
                      y={nutritionGoals.fiber}
                      stroke={NUTRITION_SERIES_COLORS.fiber}
                      strokeDasharray="4 4"
                      label={{
                        value: "Fiber goal",
                        position: "insideTopRight",
                        fill: NUTRITION_SERIES_COLORS.fiber,
                      }}
                    />
                  )}
              </LineChart>
            </ResponsiveContainer>
          )}
          <ProgressSectionFooter onBackToTop={scrollToProgressTop} />
        </CardContent>
      </Card>

      {/* Weekly reports */}
      <Card
        id="progress-section-reports"
        sx={{
          mb: 3,
          boxShadow: theme.shadows[1],
          scrollMarginTop: `${PROGRESS_SECTION_SCROLL_MARGIN_PX}px`,
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Weekly reports
          </Typography>
          {isLoading ? (
            emptyChart
          ) : reports.length === 0 ? (
            emptyChart
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Week</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((r) => (
                    <TableRow key={r.reportId || r.createdOn}>
                      <TableCell>{shortDateLabel(r.createdOn)}</TableCell>
                      <TableCell>
                        {shortDateLabel(r.weekStart)} — {shortDateLabel(r.weekEnd)}
                      </TableCell>
                      <TableCell>{weeklyReportStatusLabel(r.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <ProgressSectionFooter onBackToTop={scrollToProgressTop} />
        </CardContent>
      </Card>
    </Container>
  );
};

export default ClientProgressPage;
